import { useEffect, useState, useCallback, useRef } from "react";
import { Send, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";

export default function Messages({ openWith }) {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const endRef = useRef(null);

  const loadThreads = useCallback(() => api.get("/messages").then((r) => setThreads(r.data.threads)).catch(() => {}), []);
  const openThread = useCallback((email, name) => {
    setActive({ email, name });
    api.get("/messages/thread", { params: { with_email: email } }).then((r) => setMsgs(r.data.messages)).catch(() => {});
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { if (openWith?.email) openThread(openWith.email, openWith.name); }, [openWith, openThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!body.trim() || !active) return;
    await api.post("/messages", { to_email: active.email, to_name: active.name, body }).catch(() => {});
    setBody(""); openThread(active.email, active.name); loadThreads();
  };

  const me = (user?.email || "").toLowerCase();

  return (
    <div className="grid md:grid-cols-3 gap-0 bg-white rounded-2xl border border-slate-200 overflow-hidden min-h-[420px]" data-testid="messages">
      <div className="border-r border-slate-100 md:col-span-1">
        <div className="px-4 py-3 border-b border-slate-100 font-semibold text-blue-950 text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4 text-teal-600" /> Conversations</div>
        {threads.length === 0 && <p className="text-sm text-slate-400 p-4">No conversations yet.</p>}
        {threads.map((t) => (
          <button key={t.thread_id} onClick={() => openThread(t.with_email, t.with_name)} data-testid={`msg-thread-${t.with_email}`}
            className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 ${active?.email === t.with_email ? "bg-teal-50" : ""}`}>
            <div className="flex items-center justify-between"><span className="font-medium text-sm text-blue-950">{t.with_name}</span>{t.unread > 0 && <span className="bg-amber-500 text-white text-[10px] rounded-full px-1.5">{t.unread}</span>}</div>
            <p className="text-xs text-slate-500 truncate">{t.last}</p>
          </button>
        ))}
      </div>
      <div className="md:col-span-2 flex flex-col">
        {!active ? (
          <div className="flex-1 grid place-items-center text-slate-400 text-sm p-6">Select a conversation to start messaging.</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-slate-100 font-semibold text-blue-950 text-sm">{active.name}</div>
            <div className="flex-1 p-4 space-y-2 overflow-y-auto max-h-[320px]" data-testid="msg-thread-view">
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.from_email === me ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.from_email === me ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-800"}`}>{m.body}</div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-slate-100 flex gap-2">
              <Input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" data-testid="msg-input" />
              <Button onClick={send} data-testid="msg-send" className="bg-teal-600 hover:bg-teal-700 gap-1.5"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
